import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { PaperclipIcon, TrashIcon } from '@blade/ui-kit'

import { FadeOverflow } from '../../utils/fade-overflow'

type TextareaAttachmentsProps = {
  attachments?: Array<string> | undefined
  onAttachmentsRemove: (index: number) => void
}

const TextareaAttachments_ = ({
  onAttachmentsRemove,
  attachments = undefined,
}: TextareaAttachmentsProps) => {
  const { t } = useTranslation()

  const handleAttachmentsRemove = (event: React.MouseEvent, index: number) => {
    event.stopPropagation()
    event.preventDefault()
    onAttachmentsRemove(index)
  }

  if (!attachments || attachments.length === 0) {
    return undefined
  }

  return (
    <Root>
      <StyledFadeOverflow variant="horizontal" inlineStart={12} inlineEnd={12}>
        <AttachmentsTrack>
          {attachments.map((attachment, index) => (
            <AttachmentsPreviewItem
              key={index}
              onClick={(event) => handleAttachmentsRemove(event, index)}
            >
              <AttachmentsPreviewImage
                src={attachment}
                alt={t('comment.image_attachment')}
                aria-selected={index === 0}
              />
              <RemoveButton size={14} />
            </AttachmentsPreviewItem>
          ))}
        </AttachmentsTrack>
      </StyledFadeOverflow>
    </Root>
  )
}

const AttachmentsInput = ({
  onAttachmentsAdd,
}: {
  onAttachmentsAdd: (attachments: Array<string>) => void
}) => {
  const { t } = useTranslation()

  const { getRootProps, getInputProps, open } = useDropzone({
    noDrag: true,
    noClick: true,
    multiple: true,
    accept: { 'image/*': [] },
    onDrop: (acceptedFiles) => {
      onAttachmentsAdd(acceptedFiles.map((file) => URL.createObjectURL(file)))
    },
  })

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    open()
  }

  return (
    <ToolButton {...getRootProps()} onClick={handleClick}>
      <input {...getInputProps()} />
      <AttachmentsIcon width={15} height={15} />
      <span
        style={{
          display: 'grid',
          placeContent: 'center',
          marginBottom: '-1px',
        }}
      >
        {t('common.add_file')}
      </span>
    </ToolButton>
  )
}

TextareaAttachments_.AttachmentsInput = AttachmentsInput

export const TextareaAttachments = TextareaAttachments_

const Root = styled.div`
  border-top: 1px solid var(--background-2);
  padding-top: 10px;
  padding-bottom: 2px;
  overflow: hidden;
`

const ToolButton = styled.span`
  color: var(--text-8);

  display: grid;
  grid-auto-flow: column;
  gap: 3px;
  cursor: pointer;

  transition: color 0.15s ease-in-out;

  &:hover {
    color: var(--text-11);
  }
`

const AttachmentsIcon = styled(PaperclipIcon)`
  width: 15px;
  height: 15px;
`

const StyledFadeOverflow = styled(FadeOverflow)`
  && {
    overflow-x: auto;
    scrollbar-width: none;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

const AttachmentsTrack = styled.div`
  display: flex;
  gap: 4px;
  width: max-content;
`

const AttachmentsPreviewItem = styled.div`
  position: relative;
  display: grid;
  flex-shrink: 0;
`
const AttachmentsPreviewImage = styled.img`
  width: 28px;
  height: 28px;

  border-radius: 4px;
  opacity: 0.6;
  transition: opacity 0.15s ease-in-out;
  cursor: pointer;
  position: relative;

  ${AttachmentsPreviewItem}:hover & {
    opacity: 1;
  }
`

const RemoveButton = styled(TrashIcon)`
  color: var(--text-11);
  background: rgb(0, 0, 0, 0.9);

  position: absolute;
  cursor: pointer;
  place-self: center;
  transition: all 0.15s ease-in-out;

  opacity: 0;
  border-radius: 50%;
  padding: 2px;

  ${AttachmentsPreviewItem}:hover & {
    color: var(--text-12);
    opacity: 1;
  }
`
